/**
 * Hook for Supabase Realtime updates on scheduled_classes and class_bookings
 * 
 * Subscribes to database changes and triggers cache invalidation + refresh
 * Filters changes by gym_id and date range
 * 
 * @example
 * ```typescript
 * const { connectionStatus, error } = useRealtimeSchedule({
 *   enabled: true,
 *   gymId: 'gym-uuid',
 *   startDate: new Date(),
 *   endDate: addWeeks(new Date(), 1),
 *   onScheduleChange: () => handleRefresh(),
 *   onBookingChange: () => handleRefresh()
 * });
 * ```
 */

import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  RealtimeChannel,
  RealtimePostgresChangesPayload,
  REALTIME_SUBSCRIBE_STATES,
} from '@supabase/supabase-js';
import { format } from 'date-fns';
import { toast } from 'sonner';
import i18n from '@/i18n';

type ConnectionStatus = 'connected' | 'disconnected' | 'connecting';

interface ScheduleRealtimeRecord {
  class_date?: string | null;
  name?: string | null;
}

interface UseRealtimeScheduleOptions {
  enabled: boolean;
  gymId: string;
  startDate: Date;
  endDate: Date;
  onScheduleChange?: () => void;
  onBookingChange?: () => void;
  showNotifications?: boolean; // Show toast notifications for changes
}

interface UseRealtimeScheduleReturn {
  connectionStatus: ConnectionStatus;
  error: Error | null;
}

export function useRealtimeSchedule({
  enabled,
  gymId,
  startDate,
  endDate,
  onScheduleChange,
  onBookingChange,
  showNotifications = true
}: UseRealtimeScheduleOptions): UseRealtimeScheduleReturn {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [error, setError] = useState<Error | null>(null);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // ✅ Refs stables pour les callbacks pour éviter les re-créations du WebSocket
  const onScheduleChangeRef = useRef(onScheduleChange);
  const onBookingChangeRef = useRef(onBookingChange);
  
  // Mettre à jour les refs sans déclencher de re-render
  useEffect(() => {
    onScheduleChangeRef.current = onScheduleChange;
  }, [onScheduleChange]);
  
  useEffect(() => {
    onBookingChangeRef.current = onBookingChange;
  }, [onBookingChange]);

  useEffect(() => {
    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const clearRefreshTimer = () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };

    if (!enabled || !gymId) {
      clearReconnectTimer();
      clearRefreshTimer();
      setConnectionStatus('disconnected');
      return;
    }

    console.log('[useRealtimeSchedule] Setting up Realtime subscription for gym:', gymId);
    setConnectionStatus('connecting');

    const startStr = format(startDate, 'yyyy-MM-dd');
    const endStr = format(endDate, 'yyyy-MM-dd');

    // Nom déterministe : évite d'empiler des canaux différents à chaque remontage
    // et rend l'état Realtime stable sur mobile après veille/navigation.
    const channelName = `schedule-realtime-${gymId}-${startStr}-${endStr}`;
    const channel = supabase.channel(channelName);

    // Comparaison stricte jour-pour-jour sur la chaîne yyyy-MM-dd
    // (évite les ratés aux bornes quand startDate/endDate portent une heure)
    const isDateInRange = (dateStr?: string | null): boolean => {
      if (!dateStr) return false;
      const d = dateStr.slice(0, 10);
      return d >= startStr && d <= endStr;
    };

    const getPayloadRecord = (
      payload: RealtimePostgresChangesPayload<ScheduleRealtimeRecord>
    ): ScheduleRealtimeRecord | null => {
      const record = payload.eventType === 'DELETE' ? payload.old : payload.new;
      return Object.keys(record).length > 0 ? record : null;
    };

    const triggerScheduleRefresh = () => {
      clearRefreshTimer();
      refreshTimerRef.current = setTimeout(() => {
        onScheduleChangeRef.current?.();
      }, 150);
    };

    const triggerBookingRefresh = () => {
      clearRefreshTimer();
      refreshTimerRef.current = setTimeout(() => {
        onBookingChangeRef.current?.();
      }, 150);
    };

    const handleConnectionFailure = (message: string) => {
      console.warn('[useRealtimeSchedule] Connection issue:', message);
      setConnectionStatus('disconnected');
      setError(new Error(message));

      clearReconnectTimer();
      reconnectTimerRef.current = setTimeout(() => {
        console.log('[useRealtimeSchedule] Reconnecting after issue');
        setReconnectAttempt((attempt) => attempt + 1);
      }, 3000);
    };

    // Subscribe to scheduled_classes changes
    channel
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'scheduled_classes',
          filter: `gym_id=eq.${gymId}` // Server-side filtering by gym_id
        },
        (payload) => {
          console.log('[useRealtimeSchedule] scheduled_classes change:', payload);

          // Client-side filtering by date range
          const record = getPayloadRecord(payload);
          if (!record || !isDateInRange(record.class_date)) {
            console.log('[useRealtimeSchedule] Change outside date range, ignoring');
            return;
          }

          // Les réservations touchent `scheduled_classes.updated_at` côté DB :
          // ce signal est visible par adhérents/admins/coachs sans exposer les
          // lignes `class_bookings` des autres adhérents.
          triggerScheduleRefresh();

          // Show notification
          if (showNotifications) {
            if (payload.eventType === 'INSERT') {
              toast.info(i18n.t('common:toasts.newClassesAvailable'), {
                description: i18n.t('common:toasts.classAdded', { name: record.name })
              });
            } else if (payload.eventType === 'DELETE') {
              toast.info(i18n.t('common:toasts.scheduleUpdated'), {
                description: i18n.t('common:toasts.classDeleted')
              });
            } else if (payload.eventType === 'UPDATE') {
              toast.info(i18n.t('common:toasts.scheduleUpdated'), {
                description: i18n.t('common:toasts.classModified', { name: record.name })
              });
            }
          }
        }
      )
      // Subscribe to class_bookings changes (to update available spots)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'class_bookings',
          filter: `gym_id=eq.${gymId}` // Server-side filtering by gym_id
        },
        (payload) => {
          console.log('[useRealtimeSchedule] class_bookings change:', payload);

          // Client-side filtering by date range
          const record = getPayloadRecord(payload);
          if (!record || !isDateInRange(record.class_date)) {
            console.log('[useRealtimeSchedule] Booking change outside date range, ignoring');
            return;
          }

          triggerBookingRefresh();

          // Note: We don't show toast for bookings to avoid spamming
        }
      )
      // Subscribe to trial_prospects changes (also consume slots)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trial_prospects',
          filter: `gym_id=eq.${gymId}`
        },
        (payload) => {
          console.log('[useRealtimeSchedule] trial_prospects change:', payload);
          const record = getPayloadRecord(payload);
          if (!record || !isDateInRange(record.class_date)) {
            return;
          }
          triggerBookingRefresh();
        }
      )
      .subscribe((status) => {
        console.log('[useRealtimeSchedule] Subscription status:', status);
        
        if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
          clearReconnectTimer();
          setConnectionStatus('connected');
          setError(null);
        } else if (status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR) {
          handleConnectionFailure('Failed to connect to Realtime');
        } else if (status === REALTIME_SUBSCRIBE_STATES.TIMED_OUT) {
          handleConnectionFailure('Realtime connection timed out');
        } else if (status === REALTIME_SUBSCRIBE_STATES.CLOSED) {
          handleConnectionFailure('Realtime connection closed');
        }
      });

    channelRef.current = channel;

    // Cleanup
    return () => {
      console.log('[useRealtimeSchedule] Cleaning up Realtime subscription');
      clearReconnectTimer();
      clearRefreshTimer();
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      setConnectionStatus('disconnected');
    };
  }, [enabled, gymId, startDate?.getTime(), endDate?.getTime(), showNotifications, reconnectAttempt]);
  // ✅ onScheduleChange et onBookingChange RETIRÉS des dépendances

  return {
    connectionStatus,
    error
  };
}
